import React, { useEffect, useState, useCallback } from 'react';
import { artifactoryService } from '../../services/artifactoryService';
import { useTheme } from '../../contexts/ThemeContext';

interface BlueprintGenesisModalProps {
  open: boolean;
  sessionId: string | null;
  intent: string;
  onClose: () => void;          // Cancel / backdrop — dismiss, do nothing
  onKeepRefining: () => void;   // distinct from onClose — refocus the spine compose
  onGenerating: () => void;     // genesis dispatched — caller refetches the binding
}

/**
 * Living Blueprint SP2b — the editable cost-gate before genesis. Belt-and-
 * suspenders (decision 3): the intent is an editable textarea prepopulated from
 * the spine-shaped intent, so the user can tighten it one last time. A rough,
 * read-only estimate (no model call) frames the spend. No dead-end: [Keep
 * refining] returns to the conversation; [Generate] dispatches and reacts to a
 * 409 (already germinating) inline.
 */
export const BlueprintGenesisModal: React.FC<BlueprintGenesisModalProps> = ({
  open, sessionId, intent, onClose, onKeepRefining, onGenerating,
}) => {
  const { darkMode } = useTheme();
  const [edited, setEdited] = useState(intent);
  const [estimate, setEstimate] = useState<{ node_count_hint: number; token_est: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-seed the textarea whenever the modal (re)opens with a fresh intent.
  useEffect(() => { if (open) { setEdited(intent); setError(null); } }, [open, intent]);

  // Estimate off the edited text on a short idle (cheap, read-only — no model).
  useEffect(() => {
    if (!open || !sessionId) return;
    let alive = true;
    const t = setTimeout(() => {
      artifactoryService.genesisEstimate(sessionId, edited)
        .then((e) => { if (alive) setEstimate(e); })
        .catch(() => { /* estimate is advisory — never block the gate on it */ });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [open, sessionId, edited]);

  const onGenerate = useCallback(async () => {
    if (!sessionId || busy || !edited.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await artifactoryService.generateBlueprint(sessionId, edited);
      onGenerating();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        setError('This blueprint is already generating.');
      } else {
        setError('Could not start generation. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [sessionId, busy, edited, onGenerating]);

  if (!open) return null;

  return (
    <div data-testid="genesis-backdrop"
         className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {/* Backdrop is intentionally inert: a stray outside click must NOT dismiss
          a brief built across a multi-set funnel (lost-the-work bug). Dismiss is
          explicit only — ✕, Keep refining, or Generate. */}
      <div
        role="dialog"
        aria-label="Generate blueprint"
        className={`w-[520px] max-h-[80vh] overflow-y-auto rounded-lg shadow-xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <span aria-hidden>🌱</span>
            <span className="font-semibold">Generate this blueprint</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-600/30" aria-label="Close">✕</button>
        </div>

        <div className="p-4">
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Project intent (edit to tighten before building)
          </label>
          <textarea
            data-testid="genesis-intent"
            value={edited}
            onChange={(e) => setEdited(e.target.value)}
            rows={5}
            className={`w-full rounded px-3 py-2 text-sm resize-y border ${darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
          />

          <div className={`mt-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {estimate
              ? <>Rough estimate: <strong>~{estimate.node_count_hint}</strong> files · ~{estimate.token_est.toLocaleString()} tokens <span className="opacity-70">(ballpark)</span></>
              : 'Estimating…'}
          </div>

          {error && (
            <div className="mt-3 text-sm rounded px-3 py-2 bg-rose-500/15 text-rose-400">{error}</div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              data-testid="genesis-keep-refining"
              onClick={onKeepRefining}
              className={`px-3 py-1.5 rounded text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
            >
              Keep refining
            </button>
            <button
              data-testid="genesis-generate"
              onClick={onGenerate}
              disabled={busy || !edited.trim()}
              className={`px-3 py-1.5 rounded text-sm font-medium text-white ${busy || !edited.trim() ? 'bg-emerald-600/50 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-500'}`}
            >
              {busy ? 'Starting…' : '✨ Generate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlueprintGenesisModal;
